import DeleteIcon from '@mui/icons-material/Delete';
import { Box, DialogContent, Grid, Tooltip, styled, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Cell } from 'react-table';
import {
  getJobBoardSessionSettings,
  deleteByPrismSessionId,
  getAllPrismSession,
  IPrismSessionDTO,
} from 'wisestep-api';
import {
  Label,
  WsButton,
  WsColors,
  WsDataTable,
  WsDialog,
  WsSelectObj,
  Item,
  WsDrawer,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import UserActionTable from '../users/components/UserActionTable';
const StyledStack = styled(Stack)({
  padding: '2',
  backgroundColor: WsColors.TheBlues50,
  gap: 3,
});
const UserSession = (refreshActionCount) => {
  const [indexNo, setIndexNo] = useState<number>(-1);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [sessionId, setSessionId] = useState('choose');
  const [configuredSession, setConfiguredSession] = useState<IPrismSessionDTO[]>([]);
  const [isDataRetrieved, setIsDataRetrieved] = useState<boolean>(false);
  const [openActionDrawer, setOpenActionDrawer] = useState<boolean>(false);
  const [moduleName, setModuleName] = useState<string>('CLIENT');
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  useEffect(() => {
    const fetchSession = async () => {
      const response = await getAllPrismSession();
      if (response.data) {
        const sessionData = response.data;
        setConfiguredSession(sessionData);
        setIsDataRetrieved(true);
      }
    };
    if (!isDataRetrieved) {
      fetchSession();
    }
  }, [isDataRetrieved]);
  useEffect(() => {
    (async function () {
      const tempArray: WsSelectObj[] = [{ label: 'choose...', value: 'choose' }];
      const getJobBoardSessionSettingsData = await getJobBoardSessionSettings();
      if (getJobBoardSessionSettingsData.data) {
        getJobBoardSessionSettingsData.data.map((list) =>
          tempArray.push({ label: list.jobBoard, value: list.jobBoard })
        );
      }
    })();
  }, []);
  const formDefValues = {
    selectSession: sessionId,
  };
  const formOptions = {
    defaultValues: formDefValues,
  };
  const methods = useForm(formOptions);
  const { watch } = methods;
  const selectedSession = watch('selectSession');
  useEffect(() => {
    if (selectedSession) {
      apiCallHandlerFetchSession(selectedSession);
    }
  }, [selectedSession]);
  const apiCallHandlerFetchSession = async (sId: string) => {
    if (sId === 'choose') {
      setSessionId(sId);
    } else {
      setSessionId(sId);
    }
  };
  const refresh = async () => {
    const response = await getAllPrismSession();
    if (response.data) {
      const sessionData = response.data;
      setConfiguredSession(sessionData);
      setIsDataRetrieved(true);
    }
  };
  const deleteHandler = async (index: number) => {
    if (configuredSession) {
      try {
        const tempData: IPrismSessionDTO[] = configuredSession;
        await deleteByPrismSessionId(configuredSession[index].id);
        setNotification(success('Deleted Successfully.'));
        tempData.splice(index, 1);
        setConfiguredSession([...tempData]);
        setConfirmDelete(false);
        setIndexNo(-1);
      } catch (error) {
        setNotification(warning('Something went wrong, Please try again.'));
      }
    }
  };
  const columns = [
    {
      Header: 'Sl No',
      accessor: 'slno',
    },
    {
      Header: 'Login ID',
      accessor: 'loginID',
    },
    {
      Header: 'Jobboard Name',
      accessor: 'jobboardName',
    },
    {
      Header: 'Active Logins',
      accessor: 'activeLogins',
    },
    {
      Header: 'Available Logins',
      accessor: 'availableLogins',
    },
    {
      Header: 'Action',
      id: 'actions',
      Cell: (cell: Cell) => {
        return (
          <Tooltip title={'Delete'}>
            <DeleteIcon
              onClick={() => {
                setIndexNo(cell.row.index);
                setConfirmDelete(true);
              }}
              sx={{ fill: WsColors.Red500, cursor: 'pointer' }}
            />
          </Tooltip>
        );
      },
    },
  ];
  const openDrawer = (email: string, module: string) => {
    setSelectedEmail(email);
    setModuleName(module);
    setOpenActionDrawer(true);
  };
  const onActionDrawerClose = () => {
    setOpenActionDrawer(false);
    setSelectedEmail('');
  };
  const getBannerName = (module: string) => (module === 'USER_SESSION' ? 'Active Logins' : '');
  const tableData = useMemo(
    () =>
      configuredSession?.map((list, index) => ({
        slno: index + 1,
        loginID: list.loginId,
        jobboardName: list.jobBoard,
        activeLogins: list.users ? (
          <WsButton variant="outlined" onClick={() => openDrawer(list.id ?? '', 'USER_SESSION')}>
            {list.users.length.toString()}
          </WsButton>
        ) : (
          <Tooltip title={list.createdBy} placement="top">
            <Label> {`list.users.length`}</Label>
          </Tooltip>
        ),
        availableLogins: list.locks - list.users.length,
      })),
    [configuredSession]
  );
  return (
    <Box marginBottom={3}>
      <FormProvider {...methods}>
        <StyledStack direction="row" justifyContent="space-between" marginTop={-2}>
          <Item>
            <WsTypography variant={WsTypoVariants.TITLE}>Session List</WsTypography>
          </Item>
          <Item>
            <WsButton onClick={refresh} variant="contained" size="medium">
              Refresh
            </WsButton>
          </Item>
        </StyledStack>
        <Grid marginTop={0} container>
          <WsDataTable columns={columns} data={tableData ?? []} />
        </Grid>
      </FormProvider>
      {confirmDelete && (
        <WsDialog openDialog={true} size="small">
          <DialogContent>
            <Grid alignItems="center" container spacing={1} direction="column">
              <Grid item xs={12}>
                <Label sx={{ fontWeight: 'bold' }}>
                  Are you sure you want to delete this Session Login?
                </Label>
              </Grid>
              <Grid
                item
                width="40%"
                display="flex"
                justifyContent="space-between"
                alignSelf="center"
              >
                <WsButton onClick={() => deleteHandler(indexNo)}>Yes</WsButton>
                <WsButton
                  onClick={() => {
                    setIndexNo(-1);
                    setConfirmDelete(false);
                  }}
                >
                  No
                </WsButton>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>
      )}
      {openActionDrawer && (
        <WsDrawer
          open={openActionDrawer}
          onClose={onActionDrawerClose}
          title={getBannerName(moduleName)}
          headerSize="medium"
          size="small"
        >
          <UserActionTable
            email={selectedEmail}
            moduleName={moduleName}
            closeDrawer={onActionDrawerClose}
            refeshCount={refreshActionCount}
          ></UserActionTable>
        </WsDrawer>
      )}
    </Box>
  );
};
export default UserSession;
